// ---------------------------------------------------------------------------
// Rollouts — playing a few rounds past this one, quickly
// ---------------------------------------------------------------------------
//
// The search bot's look-ahead: after this round resolves, both sides play on
// for a few rounds with rolloutPolicy, so a plan is judged by where the war
// stands then — what building up, a protected brood economy or a well-timed
// strike are worth, which one round can't see.
//
// rolloutPolicy(me, them, rng) → orders. Cheap and legal, roughly sensible:
//   - broods wherever two veterans stand (half the time), deepest row
//   - deploys: first where the opponent matches or beats it, else onto its
//     biggest stack with room
//   - attacks only with a 2+ edge over everything the opponent has there,
//     aimed where the crater covers the most live structure cells
//   - places the drop zones it owes; buys nothing
// Inside the rollout it plays with both Grids in view (the opponent's is a
// sample): an optimistic shortcut that keeps rollouts fast.

import { rules } from '../config.js'
import {
    deployArmy, buildFort, canAttack, canPlaceDropZone, requiredDropZones, calculateCombatStrength,
} from '../gameLogic.js'
import { eligibleBaseCenters } from '../subplotBases.js'
import { plotSubIndices, plotOf, isStructureDestroyed } from '../subplotGrid.js'
import { craterFootprint } from '../subplotDamage.js'
import { validateOrders } from '../validateOrders.js'
import { openSlots, deployOnly } from './randomBot.js'

const clone = (v) => JSON.parse(JSON.stringify(v))
const room = (bf) => bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))
const strength = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0)
const corridorCells = (col) => [0, 1, 2, 3, 4].flatMap(row => plotSubIndices(col, row))

// Live (non-brood) structure cells of a Grid, as a Set.
function liveCells(hf) {
    const out = new Set()
    for (const [id, st] of Object.entries(hf?.structures ?? {})) {
        if (isStructureDestroyed(hf, id)) continue
        for (const s of st.footprint) if (!hf.subplots[s].destroyed) out.add(s)
    }
    return out
}

// `aimOdds` (optional): per-cell odds of a live structure on the opponent's
// Grid as this player actually believes them; without, it aims at the
// opponent's real structures (fine for the opponent's side of the rollout —
// the bot's own Grid is no guess).
export function rolloutPolicy(me, them, rng, aimOdds = null) {
    let board = clone(me.board)
    const theirs = them.board
    const hf = me.homefront

    // Broods.
    for (let c = 0; c < board.length; c++) {
        if ((board[c].armyQty || 0) < rules().broodCost || board[c].isAttacking || rng() < 0.5) continue
        const spots = eligibleBaseCenters(hf, c)
        if (!spots.length) continue
        const r = buildFort(board, board[c].id, spots[spots.length - 1])
        if (r.success) board = r.boardArr
    }
    // Deploys.
    let owed = rules().armiesPerRound
    for (let guard = 0; owed > 0 && guard < 12; guard++) {
        const cols = board.map((_, c) => c).filter(c => room(board[c]) > 0 && !board[c].isAttacking)
        if (!cols.length) break
        const matched = cols.filter(c => strength(theirs[c]) > 0 && strength(theirs[c]) >= strength(board[c]))
        const c = matched.length ? matched[Math.floor(rng() * matched.length)]
            : cols.reduce((a, b) => (strength(board[b]) > strength(board[a]) ? b : a))
        const r = deployArmy(board, board[c].id, owed)
        if (!r.success) break
        board = r.boardArr; owed = r.armiesToDeploy
    }
    // Attacks with a clear edge, aimed for the most live structure cells.
    const targets = aimOdds ? null : liveCells(them.homefront)
    const worth = (s) => (aimOdds ? (them.homefront?.subplots?.[s]?.destroyed ? 0 : aimOdds[s] ?? 0) : (targets.has(s) ? 1 : 0))
    const targetPlots = Array(board.length).fill(null)
    const targetSubplots = Array(board.length).fill(null)
    for (let c = 0; c < board.length; c++) {
        if (!canAttack(board[c]).valid) continue
        const margin = calculateCombatStrength(board[c]) - strength(theirs[c])
        if (margin < 2) continue
        let best = corridorCells(c)[7], bestHit = -1
        for (const s of corridorCells(c)) {
            const hit = craterFootprint(s, Math.min(5, margin)).reduce((a, x) => a + worth(x), 0)
            if (hit > bestHit) { best = s; bestHit = hit }
        }
        board[c] = { ...board[c], isAttacking: true }
        targetSubplots[c] = best
        targetPlots[c] = plotOf(best).row
    }
    // Drop zones owed.
    const dropZones = []
    const owedZones = Math.min(requiredDropZones(board), openSlots(board))
    for (let c = 0; dropZones.length < owedZones && c < board.length; c++) {
        while (dropZones.length < owedZones && canPlaceDropZone(board, c, dropZones).valid) dropZones.push(c)
    }
    const orders = { board, targetPlots, targetSubplots, dropZones, rockets: [], drones: [] }
    return validateOrders(me, orders).ok ? orders : deployOnly(me)
}
